import { useMemo } from 'react';
import { FunctionField, TextField, TopToolbar } from 'react-admin';

import {
  GetMigratedCampaignsQuery,
  GetMigratedCampaignsResponse,
  getMigratedCampaignsSortColumns,
} from '@Common/types/bo/b2b/campaigns/migrated';

import { DatagridTyped, FilterButton, List } from '@sh4res/bo-common/components';
import {
  b2bMigratedCampaignStatusChoices,
  b2bMigratedCampaignSubtypeChoices,
} from '@sh4res/bo-common/components/B2bCampaigns/choices';
import { SelectColumnsButton } from '@sh4res/bo-common/components/SelectColumnsButton';
import {
  ChipFieldsList,
  CurrencyField,
  IdField,
  SelectChipField,
  TextArrayInput,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import { DateInput } from '@sh4res/bo-common/fields/DateInput';
import { ResourceBasePath, useResourceBasePath, useSelectedColumns } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort, Resources } from '@sh4res/bo-common/utils';
import { createFilters, FilterList } from '@sh4res/bo-common/utils/create_filters';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type MigratedCampaignItem = GetMigratedCampaignsResponse[number] & { reference_period: string };

const testId = 'B2bMigratedCampaignsList';

const operationDateLabel = 'resources.bo_common.b2b.campaigns.migrated.fields.operation_date';

const defaultSort: RaSort<GetMigratedCampaignsQuery> = { field: 'created_at', order: 'DESC' };

// On the external BO the subtype filter is hidden and forced to this hardcoded set of values.
const subtypeFilter = [
  'RSP',
  'INT',
  'PPV',
  'VVENT',
  'ECHLEG',
  'ABD',
  'INT_CCB',
  'GAG_CASH',
  'GAG_TITR',
];

const filtersInput = () =>
  createFilters<GetMigratedCampaignsQuery>({
    id: <TextArrayInput source="" testId={`${testId}_IdInput`} />,
    b2b_company_id: <TextArrayInput asNumber source="" testId={`${testId}_CompanyIdInput`} />,
    status: <TextArrayInput source="" testId={`${testId}_StatusInput`} />,

    from_created_at: <DateInput source="" data-testid={`${testId}_FromCreatedAtInput`} />,
    to_created_at: <DateInput source="" data-testid={`${testId}_ToCreatedAtInput`} />,
  } as FilterList<GetMigratedCampaignsQuery>);

const migratedCampaignsListColumns = (
  resourceBasePath: ResourceBasePath,
  isExternal: boolean,
): ColumnList<MigratedCampaignItem> => ({
  id: (
    <IdField
      reference={`${resourceBasePath}campaigns/migrated`}
      label="resources.bo_common.b2b.campaigns.migrated.fields.id"
      data-testid={`${testId}_IdField`}
    />
  ),
  b2b_company_id: (
    <IdField
      label="resources.bo_common.b2b.campaigns.migrated.fields.b2b_company_id"
      reference={`${resourceBasePath}companies`}
      data-testid={`${testId}_CompanyIdField`}
    />
  ),
  company_name: (
    <TextField
      label="resources.bo_common.b2b.campaigns.migrated.fields.company_name"
      data-testid={`${testId}_CompanyNameField`}
    />
  ),
  status: (
    <SelectChipField
      label="resources.bo_common.b2b.campaigns.migrated.fields.status"
      choices={b2bMigratedCampaignStatusChoices}
      displayRawValueIfChoiceNotFound
      data-testid={`${testId}_StatusField`}
    />
  ),
  type: (
    <TextField
      label="resources.bo_common.b2b.campaigns.migrated.fields.type"
      data-testid={`${testId}_TypeField`}
    />
  ),
  subtype: (
    <SelectChipField
      label="resources.bo_common.b2b.campaigns.migrated.fields.subtype"
      choices={b2bMigratedCampaignSubtypeChoices}
      displayRawValueIfChoiceNotFound
      data-testid={`${testId}_SubtypeField`}
    />
  ),
  created_at: (
    <UTCDateField
      label="resources.bo_common.b2b.campaigns.migrated.fields.created_at"
      showTime={false}
      data-testid={`${testId}_CreatedAtField`}
    />
  ),
  fiscal_period_start: (
    <UTCDateField
      label="resources.bo_common.b2b.campaigns.migrated.fields.fiscal_period_start"
      showTime={false}
      data-testid={`${testId}_FiscalPeriodStartField`}
    />
  ),
  fiscal_period_end: (
    <UTCDateField
      label="resources.bo_common.b2b.campaigns.migrated.fields.fiscal_period_end"
      showTime={false}
      data-testid={`${testId}_FiscalPeriodEndField`}
    />
  ),
  reference_period: (
    <FunctionField
      label="resources.bo_common.b2b.campaigns.migrated.fields.reference_period"
      data-testid={`${testId}_ReferencePeriodField`}
      render={(record: MigratedCampaignItem) => (
        <>
          <UTCDateField
            record={record}
            source="fiscal_period_start"
            showTime={false}
            data-testid={`${testId}_ReferencePeriodStartField`}
          />
          -
          <UTCDateField
            record={record}
            source="fiscal_period_end"
            showTime={false}
            data-testid={`${testId}_ReferencePeriodEndField`}
          />
        </>
      )}
    />
  ),
  processing_start_date: (
    <UTCDateField
      label="resources.bo_common.b2b.campaigns.migrated.fields.processing_start_date"
      showTime={false}
      // External BO displays this column simply as "Date".
      {...(isExternal ? { label: operationDateLabel } : {})}
      data-testid={`${testId}_ProcessingStartDateField`}
    />
  ),
  processing_end_date: (
    <UTCDateField
      label="resources.bo_common.b2b.campaigns.migrated.fields.processing_end_date"
      showTime={false}
      data-testid={`${testId}_ProcessingEndDateField`}
    />
  ),
  amount_brut: (
    <CurrencyField
      label="resources.bo_common.b2b.campaigns.migrated.fields.amount_brut"
      variant="micro"
      currency="EUR"
      testId={`${testId}_AmountBrutField`}
    />
  ),
  amount_net: (
    <CurrencyField
      label="resources.bo_common.b2b.campaigns.migrated.fields.amount_net"
      variant="micro"
      currency="EUR"
      testId={`${testId}_AmountNetField`}
    />
  ),
  source_instrument: (
    <TextField
      label="resources.bo_common.b2b.campaigns.migrated.fields.source_instrument"
      data-testid={`${testId}_SourceInstrumentField`}
    />
  ),
  target_instrument: (
    <TextField
      label="resources.bo_common.b2b.campaigns.migrated.fields.target_instrument"
      data-testid={`${testId}_TargetInstrumentField`}
    />
  ),
  source_plan_piloted: (
    <TextField
      label="resources.bo_common.b2b.campaigns.migrated.fields.source_plan_piloted"
      data-testid={`${testId}_SourcePlanPilotedField`}
    />
  ),
  source_plan_libre: (
    <TextField
      label="resources.bo_common.b2b.campaigns.migrated.fields.source_plan_libre"
      data-testid={`${testId}_SourcePlanLibreField`}
    />
  ),
  target_plan_piloted: (
    <TextField
      label="resources.bo_common.b2b.campaigns.migrated.fields.target_plan_piloted"
      data-testid={`${testId}_TargetPlanPilotedField`}
    />
  ),
  target_plan_libre: (
    <TextField
      label="resources.bo_common.b2b.campaigns.migrated.fields.target_plan_libre"
      data-testid={`${testId}_TargetPlanLibreField`}
    />
  ),
  plan_types: <ChipFieldsList source="plan_types" />,
});

const defaultColumnsInternal: (keyof MigratedCampaignItem)[] = [
  'id',
  'subtype',
  'processing_start_date',
  'processing_end_date',
  'status',
  'reference_period',
];

const defaultColumnsExternal: (keyof MigratedCampaignItem)[] = [
  'id',
  'processing_start_date',
  'amount_brut',
  'amount_net',
  'subtype',
  'status',
  'created_at',
];

export const MigratedCampaignsListView = () => {
  const resourceBasePath = useResourceBasePath();
  const resource = `${resourceBasePath}campaigns/migrated` as Resources;
  const isExternal = isBoExternal();

  const defaultColumns = isExternal ? defaultColumnsExternal : defaultColumnsInternal;

  const displayedColumns = useSelectedColumns<MigratedCampaignItem>({
    preferences: 'b2b_campaigns.migrated.list.columns',
    defaultColumns,
  });

  const columnRegistry = useMemo(
    () => migratedCampaignsListColumns(resourceBasePath, isExternal),
    [resourceBasePath, isExternal],
  );

  return (
    <List
      resource={resource}
      actions={
        <TopToolbar>
          <FilterButton data-testid={`${testId}_FilterButton`} />
          <SelectColumnsButton
            preference="b2b_campaigns.migrated.list.columns"
            columns={columnRegistry}
            defaultColumns={defaultColumns}
            testId={`${testId}_SelectColumnsButton`}
          />
        </TopToolbar>
      }
      exporter={false}
      sort={defaultSort}
      filters={filtersInput()}
      filter={{ subtype: subtypeFilter }}
      disableSyncWithLocation={true}
      empty={false}
      sx={{
        '& .RaList-main': {
          width: '100%',
        },
      }}>
      <DatagridTyped<MigratedCampaignItem>
        rowClick="show"
        bulkActionButtons={false}
        columnRegistry={columnRegistry}
        displayedColumns={displayedColumns}
        sortableColumns={getMigratedCampaignsSortColumns}
      />
    </List>
  );
};
